import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Paper,
  InputAdornment,
  Divider,
} from '@mui/material';

import {
  Send as SendIcon,
  EmojiEmotions as EmojiIcon,
  AttachFile as FileIcon,
  MoreVert as MoreIcon,
} from '@mui/icons-material';

import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const ChatAtelier = ({ session, participant }) => {
  const { user } = useAuth();

  const rolePath = user?.role === 3 ? 'intervenant' : 'admin';

  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const scrollRef = useRef(null);

  const conversationId = session?.conversation?.id_conv;

  const authHeader = {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    },
  };

  // =========================
  // FETCH CONVERSATION
  // =========================
  const fetchConversation = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/conversation/${conversationId}`,
        authHeader
      );

      const apiMessages = res.data.messages || [];

      const formatted = apiMessages.map((msg) => {
        const isMe = msg.sender_role !== 'client';

        return {
          id: msg.id,
          text: msg.message,
          sender: isMe ? 'me' : 'client',
          time: new Date(msg.created_at).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          }),
        };
      });

      setMessages(formatted);
    } catch (err) {
      console.error('Erreur fetch conversation', err);
    }
  };

  // =========================
  // MARK AS READ (IMPORTANT)
  // =========================
  const markAsRead = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/${rolePath}/conversation/read`,
        { id_conv: conversationId },
        authHeader
      );
    } catch (err) {
      console.error('markAsRead error', err);
    }
  };

  useEffect(() => {
    if (!conversationId) return;

    fetchConversation();
    markAsRead();
  }, [conversationId]);

  // =========================
  // AUTO SCROLL
  // =========================
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // =========================
  // SEND MESSAGE
  // =========================
  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const messageToSend = inputValue;

    const tempMessage = {
      id: Date.now(),
      text: messageToSend,
      sender: 'me',
      time: new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      }),
    };

    // optimistic UI
    setMessages((prev) => [...prev, tempMessage]);
    setInputValue('');

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/${rolePath}/message/send`,
        {
          id_conv: conversationId,
          message: messageToSend,
        },
        authHeader
      );
    } catch (err) {
      console.error('Erreur envoi message', err);

      const errorMsg =
        err?.response?.data?.message || 'Message refusé par le serveur';

      // rollback
      setMessages((prev) => prev.slice(0, -1));

      // system message
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          text: errorMsg,
          sender: 'system',
          time: new Date().toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          }),
        },
      ]);
    }
  };

  // =========================
  // UI
  // =========================
  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '450px',
        bgcolor: '#FFF',
        borderRadius: '16px',
        border: '1px solid #E0E5F2',
        overflow: 'hidden',
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: '#F4F7FE',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 35, height: 35, bgcolor: '#4318FF' }}>
            {participant?.client?.prenom_client?.charAt(0) || 'C'}
          </Avatar>

          <Box>
            <Typography fontWeight={700}>
              Discussion avec {participant?.client?.prenom_client}
            </Typography>

            <Typography variant="caption" color="success.main">
              ● En ligne
            </Typography>
          </Box>
        </Box>

        <IconButton size="small">
          <MoreIcon />
        </IconButton>
      </Box>

      <Divider />

      {/* MESSAGES */}
      <Box
        ref={scrollRef}
        sx={{
          flexGrow: 1,
          p: 2,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          bgcolor: '#FBFCFF',
        }}
      >
        {messages.map((msg) => (
          <Box
            key={msg.id}
            sx={{
              alignSelf:
                msg.sender === 'me'
                  ? 'flex-end'
                  : msg.sender === 'system'
                  ? 'center'
                  : 'flex-start',
              maxWidth: '75%',
            }}
          >
            <Box
              sx={{
                p: 1.5,
                borderRadius: '16px',
                bgcolor:
                  msg.sender === 'me'
                    ? '#4318FF'
                    : msg.sender === 'system'
                    ? '#FFE6E6'
                    : '#E9EDF7',
                color:
                  msg.sender === 'me'
                    ? '#FFF'
                    : msg.sender === 'system'
                    ? '#D32F2F'
                    : '#1B2559',
              }}
            >
              <Typography variant="body2">{msg.text}</Typography>
            </Box>

            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.5,
                textAlign: 'center',
                color: '#A3AED0',
              }}
            >
              {msg.time}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* INPUT */}
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
          placeholder="Écrire un message..."
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              bgcolor: '#F4F7FE',
              '& fieldset': { border: 'none' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton size="small">
                  <EmojiIcon sx={{ color: '#A3AED0' }} />
                </IconButton>
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small">
                  <FileIcon sx={{ color: '#A3AED0' }} />
                </IconButton>

                <IconButton
                  onClick={handleSendMessage}
                  sx={{
                    bgcolor: '#4318FF',
                    color: '#FFF',
                    ml: 1,
                    '&:hover': { bgcolor: '#3311CC' },
                  }}
                >
                  <SendIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
    </Paper>
  );
};

export default ChatAtelier;